"use client";

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { isPlatformAdminRole } from "@/lib/auth/roles";
import { useAuth } from "@/lib/auth/context";

export interface ClusterOption {
  id: string;
  name: string;
  status: string;
  isK3s: boolean;
  isDefault: boolean;
  serverVersion?: string;
  nodeCount?: number;
}

interface ClusterContextType {
  selectedClusterId: string; // "all" or specific cluster ID
  setSelectedClusterId: (id: string) => void;
  clusters: ClusterOption[];
  loadingClusters: boolean;
  refreshClusters: () => Promise<void>;
  activeCluster: ClusterOption | undefined;
}

const ClusterContext = createContext<ClusterContextType | undefined>(undefined);

const STORAGE_KEY = "k8s_manager_selected_cluster_id";

export function ClusterProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [clusters, setClusters] = useState<ClusterOption[]>([]);
  const [loadingClusters, setLoadingClusters] = useState<boolean>(true);
  const [selectedClusterId, setSelectedClusterIdState] = useState<string>("all");

  const refreshClusters = useCallback(async () => {
    // Cluster inventory is platform-admin-only. Reuse the identity resolved by
    // AuthProvider instead of making a second /api/auth/me request that can
    // race login, logout, or Fast Refresh and accidentally probe /api/clusters.
    if (authLoading) return;
    if (!user || !isPlatformAdminRole(user.role)) {
      setClusters([]);
      setLoadingClusters(false);
      return;
    }

    try {
      setLoadingClusters(true);
      const res = await fetch("/api/clusters");
      if (res.ok) {
        const data = await res.json();
        const list: ClusterOption[] = Array.isArray(data) ? data : data.clusters || [];
        setClusters(list);

        // Check stored selection from localStorage
        if (typeof window !== "undefined") {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored && (stored === "all" || list.some((c) => c.id === stored))) {
            setSelectedClusterIdState(stored);
          } else if (list.length > 0) {
            // Default to "all" to show unified multi-cluster view
            setSelectedClusterIdState("all");
          }
        }
      }
    } catch {
      // Ignore network errors on init
    } finally {
      setLoadingClusters(false);
    }
  }, [authLoading, user]);

  useEffect(() => {
    void refreshClusters();
  }, [refreshClusters]);

  const setSelectedClusterId = useCallback((id: string) => {
    setSelectedClusterIdState(id);
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        // Ignore quota/storage errors
      }
    }
  }, []);

  const activeCluster = clusters.find((c) => c.id === selectedClusterId);

  return (
    <ClusterContext.Provider
      value={{
        selectedClusterId,
        setSelectedClusterId,
        clusters,
        loadingClusters,
        refreshClusters,
        activeCluster,
      }}
    >
      {children}
    </ClusterContext.Provider>
  );
}

export function useCluster() {
  const context = useContext(ClusterContext);
  if (!context) {
    throw new Error("useCluster must be used within a ClusterProvider");
  }
  return context;
}
